import * as yup from 'yup';
import { createInvoiceItemSchema } from './invoiceItem.subschema';
import { InvoiceType } from '../Types/invoicesTypes';
import { validatePolishNip } from '../utils/nipValidator';
import { invoiceLogger } from '../utils/invoiceLogger';
import {
  INVOICE_DATE_FORMAT_ERROR_MESSAGE,
  isValidInvoiceApiDate,
  normalizeInvoiceDateInput,
} from './dateValidation';

/**
 * Yup validation schema for correction invoice creation form.
 *
 * Key differences from standard invoice schema:
 * - correctionInvoiceId is REQUIRED
 * - type is fixed to InvoiceType.Correction
 * - scheme must match base invoice (validated by backend)
 * - All fields follow same rules as base invoice type
 *
 * Business Rules (enforced by backend, referenced here):
 * 1. correctionInvoiceId must exist
 * 2. Base invoice cannot be Draft or Cancelled
 * 3. Cannot correct a correction invoice
 * 4. Scheme consistency validated server-side
 * 5. Seller/Buyer auto-filled from base (can be modified)
 */
export const createCorrectionInvoiceSchema = yup.object({
  // ===================================================================
  // CORRECTION-SPECIFIC FIELDS (Required)
  // ===================================================================
  correctionInvoiceId: yup
    .number()
    .required('ID faktury bazowej jest wymagane')
    .positive('ID faktury bazowej musi być liczbą dodatnią')
    .integer('ID faktury bazowej musi być liczbą całkowitą'),

  type: yup
    .mixed()
    .oneOf([InvoiceType.Correction], 'Typ faktury musi być "Correction"')
    .required('Typ faktury jest wymagany'),

  // ===================================================================
  // BASIC FIELDS
  // ===================================================================
  reservationId: yup.number().optional().nullable(),

  currency: yup
    .string()
    .required('Wybierz walutę')
    .min(3, 'Kod waluty musi mieć 3 znaki')
    .max(3, 'Kod waluty musi mieć 3 znaki'),

  scheme: yup.mixed().required('Wybierz schemat faktury (musi być zgodny z fakturą bazową)'),

  // ===================================================================
  // DOCUMENT DATA
  // ===================================================================
  issueDate: yup
    .string()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .required('Data wystawienia jest wymagana')
    .test('issue-date-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    )
    .test('not-future', 'Data wystawienia nie może być w przyszłości', (value) => {
      if (!value) return true;

      // Parse dates in local timezone to avoid UTC midnight issues
      const issueDate = new Date(value + 'T00:00:00'); // Force local timezone
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      invoiceLogger.debug('[createCorrectionInvoiceSchema] 📅 Validating issueDate:', {
        value,
        issueDate: issueDate.toISOString(),
        issueDateTimestamp: issueDate.getTime(),
        today: today.toISOString(),
        todayTimestamp: today.getTime(),
        isValid: issueDate <= today,
        difference: issueDate.getTime() - today.getTime(),
      });

      return issueDate <= today;
    }),

  saleDate: yup
    .string()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .required('Data wykonania usługi jest wymagana')
    .test('sale-date-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    ),

  placeOfIssue: yup
    .string()
    .required('Miejsce wystawienia jest wymagane')
    .min(2, 'Miejsce wystawienia musi mieć min. 2 znaki')
    .max(100, 'Miejsce wystawienia może mieć max. 100 znaków'),

  paymentMethod: yup.mixed().required('Wybierz formę płatności'),

  bankAccount: yup
    .string()
    .required('Konto bankowe jest wymagane')
    .min(10, 'Numer konta musi mieć min. 10 znaków')
    .max(100, 'Numer konta może mieć max. 100 znaków'),

  paymentDueDate: yup
    .string()
    .nullable()
    .optional()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .test('payment-due-date-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    )
    .test('after-issue', 'Termin płatności musi być po dacie wystawienia', function (value) {
      if (!value) return true;
      const { issueDate } = this.parent;
      const normalizedIssueDate = normalizeInvoiceDateInput(issueDate);
      if (typeof normalizedIssueDate !== 'string' || !normalizedIssueDate) return true;
      return new Date(value) >= new Date(normalizedIssueDate);
    }),

  // ===================================================================
  // CORRECTION REASON (Required for corrections)
  // ===================================================================
  comments: yup
    .string()
    .required('Powód korekty jest wymagany')
    .min(10, 'Powód korekty musi mieć minimum 10 znaków')
    .max(500, 'Powód korekty może mieć maksymalnie 500 znaków'),

  // ===================================================================
  // SELLER (Required - auto-filled from base, can be modified)
  // ===================================================================
  sellerName: yup
    .string()
    .required('Nazwa sprzedawcy jest wymagana')
    .min(2, 'Nazwa sprzedawcy musi mieć min. 2 znaki')
    .max(255, 'Nazwa sprzedawcy może mieć max. 255 znaków'),

  sellerStreetNo: yup
    .string()
    .required('Ulica i numer sprzedawcy są wymagane')
    .min(2, 'Ulica i numer muszą mieć min. 2 znaki')
    .max(255, 'Ulica i numer mogą mieć max. 255 znaków'),

  sellerAddress: yup
    .string()
    .required('Adres sprzedawcy (kod, miasto) jest wymagany')
    .min(2, 'Adres musi mieć min. 2 znaki')
    .max(255, 'Adres może mieć max. 255 znaków'),

  sellerNip: yup
    .string()
    .required('NIP sprzedawcy jest wymagany')
    .test('nip-format', (value, ctx) => {
      if (!value) {
        return ctx.createError({ message: 'NIP sprzedawcy jest wymagany' });
      }

      // Clean NIP - remove dashes and spaces
      const cleaned = value.replace(/[-\s]/g, '');

      // Must be exactly 10 digits (Polish NIP format)
      if (!/^\d{10}$/.test(cleaned)) {
        return ctx.createError({
          message: 'NIP musi mieć 10 cyfr (możesz użyć myślników, np. 123-456-78-90)',
        });
      }

      // Validate checksum using MOD-11 algorithm
      if (!validatePolishNip(value)) {
        return ctx.createError({
          message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
        });
      }

      return true;
    }),

  // ===================================================================
  // BUYER (Required - auto-filled from base, can be modified)
  // ===================================================================
  buyerName: yup
    .string()
    .required('Nazwa nabywcy jest wymagana')
    .min(2, 'Nazwa nabywcy musi mieć min. 2 znaki')
    .max(255, 'Nazwa nabywcy może mieć max. 255 znaków'),

  buyerStreetNo: yup
    .string()
    .required('Ulica i numer nabywcy są wymagane')
    .min(2, 'Ulica i numer muszą mieć min. 2 znaki')
    .max(255, 'Ulica i numer mogą mieć max. 255 znaków'),

  buyerAddress: yup
    .string()
    .required('Adres nabywcy (kod, miasto) jest wymagany')
    .min(2, 'Adres musi mieć min. 2 znaki')
    .max(255, 'Adres może mieć max. 255 znaków'),

  buyerNip: yup
    .string()
    .optional()
    .nullable()
    .test('nip-format-optional', (value, ctx) => {
      // Empty/null is valid (optional field)
      if (!value || value.trim() === '') return true;

      const cleaned = value.replace(/[-\s]/g, '');
      const buyerCountryIso = String(ctx.parent?.buyerCountryIso || 'PL').toUpperCase();

      if (buyerCountryIso === 'PL') {
        if (/^PL/i.test(cleaned)) {
          return ctx.createError({
            message: 'Dla kraju PL wpisz NIP bez prefiksu kraju (np. 1234567890).',
          });
        }

        if (!/^\d{10}$/.test(cleaned)) {
          return ctx.createError({
            message: 'Dla kraju PL NIP musi mieć dokładnie 10 cyfr.',
          });
        }

        if (!validatePolishNip(cleaned)) {
          return ctx.createError({
            message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
          });
        }

        return true;
      }

      // Check if it's Polish NIP (10 digits)
      if (/^\d{10}$/.test(cleaned)) {
        // Validate checksum using MOD-11 algorithm
        if (!validatePolishNip(cleaned)) {
          return ctx.createError({
            message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
          });
        }
        return true;
      }

      // Check if it's EU VAT number (2 letters + 8-12 digits)
      if (/^[A-Z]{2}\d{8,12}$/i.test(cleaned)) {
        return true; // EU VAT format valid
      }

      // Invalid format
      return ctx.createError({
        message:
          'NIP musi mieć 10 cyfr (możesz użyć myślników) lub format VAT-UE (np. DE123456789)',
      });
    }),
  buyerCountryIso: yup
    .string()
    .required('Kod kraju nabywcy jest wymagany')
    .length(2, 'Kod kraju nabywcy musi mieć 2 znaki')
    .matches(/^[A-Za-z]{2}$/, 'Kod kraju nabywcy musi zawierać tylko litery'),

  isBuyerVatEu: yup.boolean().required('Informacja o statusie VAT-UE nabywcy jest wymagana'),
  buyerIsJst: yup.boolean().required('Informacja o statusie JST nabywcy jest wymagana'),

  // ===================================================================
  // INVOICE ITEMS (Required - at least 1 item)
  // ===================================================================
  items: yup
    .array()
    .of(createInvoiceItemSchema({ allowZeroQuantity: true }))
    .min(1, 'Dodaj przynajmniej jedną pozycję faktury korygującej')
    .required('Lista pozycji jest wymagana'),

  // ===================================================================
  // PAYMENT FIELDS (Optional for corrections - depends on base invoice type)
  // ===================================================================
  status: yup.mixed().optional().nullable(),
  paidAmount: yup.number().optional().nullable().min(0, 'Kwota zapłacona nie może być ujemna'),
  paidAt: yup
    .string()
    .optional()
    .nullable()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .test('paid-at-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    ),

  // ===================================================================
  // PREPAYMENTS (Not applicable for correction invoices)
  // ===================================================================
  selectedPrepaymentIds: yup.array().optional().default([]),

  // ===================================================================
  // PFRON REFUND FIELDS (Optional - only for specific schemes)
  // ===================================================================
  pfronRefundAmount: yup
    .number()
    .optional()
    .nullable()
    .min(0, 'Kwota zwrotu PFRON nie może być ujemna'),

  pfronRefundPercentage: yup
    .number()
    .optional()
    .nullable()
    .min(0, 'Procent zwrotu PFRON nie może być ujemny')
    .max(100, 'Procent zwrotu PFRON nie może przekroczyć 100%'),

  pfronRefundNote: yup
    .string()
    .optional()
    .nullable()
    .max(500, 'Notatka PFRON może mieć max. 500 znaków'),

  // ===================================================================
  // NOTES (Optional)
  // ===================================================================
  notes: yup.string().optional().nullable().max(1000, 'Notatki mogą mieć max. 1000 znaków'),
});

export type CreateCorrectionInvoiceFormData = yup.InferType<typeof createCorrectionInvoiceSchema>;
